import { defineStore, acceptHMRUpdate } from "pinia"
import { useOptionStore } from "./option"
import { sortBy } from "@/utils"
import type { Create, DepartmentsRecord, ItemsRecord, ItemsResponse, StoresRecord, Update } from "@/pocketbase-types"

const requestOptions = {
  expand: "department,defaultStore",
  fields: "*,expand.department.name,expand.defaultStore.name"
}

export type ItemProps = ItemsResponse<{
  department: Pick<DepartmentsRecord, "name">
  defaultStore?: Pick<StoresRecord, "name">
}>

export const useItemStore = defineStore("item", {
  state: () => ({
    items: [] as ItemProps[],
    recent: [] as ItemProps[]
  }),
  getters: {
    names: ({ items }) => items.map(i => i.name),
    favorites: ({ items }) => items.filter(i => i.favorite)
  },
  actions: {
    async refresh() {
      this.items = await this.pb.collection("items").getFullList<ItemProps>({
        ...requestOptions,
        sort: "+name"
      })
      this.recent = await this.pb.collection("items").getFullList({
        perPage: 20,
        filter: "purchasedItems_via_item.id!=null",
        expand: "department,defaultStore",
        sort: "-purchasedItems_via_item.createdAt,+name"
      })
    },
    async create(props: Omit<Create<"items">, "household">) {
      const optionStore = useOptionStore()

      const { id } = await this.pb
        .collection("items")
        .create({ ...props, household: this.pb.authStore.record?.household })
      const newItem = await this.pb.collection("items").getOne<ItemProps>(id, requestOptions)
      this.items.push(newItem)
      this.items.sort(sortBy(i => i.name))

      await optionStore.refresh()

      return newItem
    },
    async update(item: ItemsRecord, props: Update<"items">) {
      const optionStore = useOptionStore()

      await this.pb.collection("items").update(item.id, props)

      const idx = this.items.findIndex(i => i.id == item.id)
      if (idx > -1) {
        const newItem = await this.pb.collection("items").getOne<ItemProps>(item.id, requestOptions)
        this.items.splice(idx, 1, newItem)
      } else {
        // This should never happen
        await this.refresh()
      }

      await optionStore.refresh()

      return item
    },
    async delete(item: ItemsRecord) {
      await this.pb.collection("items").delete(item.id)
      const idx = this.items.findIndex(i => i.id === item.id)
      if (idx > -1) {
        this.items.splice(idx, 1)
      }
    },
    async updateTaxRates(oldRate: number, newRate: number) {
      const optionStore = useOptionStore()

      await this.pb.send("/api/updateTaxRates", { method: "POST", body: { oldRate, newRate } })
      await this.refresh()
      await optionStore.refreshTaxRates()
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useItemStore, import.meta.hot))
}
